import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as Sentry from '@sentry/react';

function CreateRoom() {
  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    if (/^[A-Z0-9 _-]{0,20}$/.test(value)) {
      setName(value);
      setNameError(value.trim().length === 0 ? 'Name cannot be empty' : null);
    }
  };

  const create = async () => {
    const trimmed = name.trim().toUpperCase();
    if (!trimmed) {
      setNameError('Name cannot be empty');
      return;
    }
    if (!/^[A-Z0-9 _-]{1,20}$/.test(trimmed)) {
      setNameError('Name can only contain letters, numbers, spaces, hyphens, or underscores (max 20)');
      return;
    }
    setSubmitting(true);
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:5000';
      const res = await axios.post<{ roomCode: string; hostToken: string }>(`${serverUrl}/api/create-room`);
      sessionStorage.setItem('hostToken', res.data.hostToken);
      sessionStorage.setItem('playerName', trimmed);
      sessionStorage.setItem('isHost', 'true');
      navigate(`/game/${res.data.roomCode}`, { state: { playerName: trimmed, isHost: true } });
    } catch (err) {
      Sentry.captureException(err);
      if (import.meta.env.MODE !== 'production') {
        console.error('Error creating room:', err);
      }
      alert('Failed to create room. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center text-center">
      <h1 className="text-4xl md:text-5xl font-bold mb-8 tracking-tight">Create a Room</h1>
      <input
        type="text"
        placeholder="Your Name"
        value={name}
        onChange={handleNameChange}
        maxLength={20}
        className={`mb-4 p-3 border rounded-lg w-full max-w-xs bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg ${
          nameError ? 'border-red-500' : 'border-gray-300'
        }`}
      />
      {nameError && <p className="text-red-400 mb-4">{nameError}</p>}
      <button
        onClick={create}
        disabled={!name.trim() || submitting}
        className={`bg-blue-600 text-white font-semibold px-8 py-4 rounded-lg shadow-md transition duration-300 text-xl ${
          !name.trim() || submitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
        }`}
      >
        {submitting ? 'Creating...' : 'Create Room'}
      </button>
      <button
        onClick={() => navigate('/')}
        className="mt-4 text-sm underline text-gray-300 hover:text-white"
      >
        Back
      </button>
    </div>
  );
}

export default CreateRoom;
